/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Panel,
  Text,
  Table,
  Margin,
  Drawer,
  ContentLoader,
} from 'shared/components'
import { observer } from 'mobx-react'
import { useInfiniteScroll } from 'shared/hooks'
import { MakeInWinAdd } from '../../../../../profile/makeInWin/overview/components/add'
import { EventsFilters } from '../filters'
import { MobileTournamentCard } from '../mobileTournamentCard'
import { MobileHeader } from '../mobileHeader'

import * as styles from './styles'

const deduplicateEvents = (content = []) =>
  content.filter(
    (event, index, array) =>
      array.findIndex((existingEvent) => existingEvent.id === event.id) === index
  )

const MobileLayoutComponent = ({
  title = 'Tournaments',
  columns,
  loadEvents,
  loadUpcomingTournaments,
  onSearch,
  appConfig,
}) => {
  const [mobileSelectedSport, setMobileSelectedSport] = useState(null)
  const [eventsData, setEventsData] = useState({ content: [], loading: false })
  const [pagination, setPagination] = useState({
    page: 0,
    hasMore: true,
    loadingMore: false,
    totalElements: 0,
  })

  // Load initial data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      if (!loadUpcomingTournaments) {
        return
      }

      setEventsData((prev) => ({ ...prev, loading: true }))
      setPagination((prev) => ({ ...prev, page: 0, hasMore: true }))

      try {
        // Use upcomings endpoint for only upcoming tournaments

        const result = await loadUpcomingTournaments(
          { page: 0, pageSize: 20 },
          null,
          { search: '', sport: null }
        )

        // Deduplicate initial content
        const content = result?.content || []
        const uniqueContent = deduplicateEvents(content)
        setEventsData({ content: uniqueContent, loading: false })

        // Use totalElements from API response to determine if there are more items
        const totalElements = result?.totalElements || 0
        const currentCount = uniqueContent.length
        const hasMoreItems = currentCount < totalElements

        setPagination((prev) => ({
          ...prev,
          hasMore: hasMoreItems,
          totalElements: totalElements,
        }))
      } catch (error) {
        console.error('Failed to load initial tournaments:', error)
        setEventsData((prev) => ({ ...prev, loading: false }))
      }
    }

    loadInitialData()
  }, [loadUpcomingTournaments])

  // Filter effect: Ensure mobile view only shows tournaments for the selected sport type
  useEffect(() => {
    if (mobileSelectedSport && eventsData.content.length > 0) {
      // Filter out tournaments that don't match the selected sport
      const filteredContent = eventsData.content.filter((event) => {
        const eventSport = event.sportType || event.tournament?.sportType
        return eventSport === mobileSelectedSport
      })

      // Only update if the content actually changed
      if (filteredContent.length !== eventsData.content.length) {
        setEventsData((prev) => ({ ...prev, content: filteredContent }))
      }
    }
  }, [eventsData.content, mobileSelectedSport])

  // Load more data for pagination - disabled since we load all tournaments at once
  const loadMoreData = useCallback(async () => {}, [pagination.totalElements])

  useInfiniteScroll({
    enabled: Boolean(loadUpcomingTournaments),
    getPagination: () => ({
      loadingMore: pagination.loadingMore,
      hasMore: pagination.hasMore,
    }),
    getContentLength: () => eventsData.content.length,
    loadMoreData,
    initialCheckDelays: [500, 2000, 5000],
  })

  // Handle mobile filter changes
  const handleMobileSportChange = async (sportType) => {
    if (!loadUpcomingTournaments) {
      return
    }

    setMobileSelectedSport(sportType)
    setEventsData((prev) => ({ ...prev, loading: true }))
    setPagination((prev) => ({
      ...prev,
      page: 0,
      hasMore: true,
      loadingMore: false,
    }))

    try {
      // Use loadUpcomingTournaments for mobile view sport filter
      const result = await loadUpcomingTournaments(
        { page: 0, pageSize: 20 },
        null,
        { search: '', sport: sportType }
      )

      // Deduplicate sport filter content
      const content = result?.content || []
      const uniqueContent = deduplicateEvents(content)
      setEventsData({ content: uniqueContent, loading: false })

      // Use totalElements from API response for sport filtering
      const totalElements = result?.totalElements || 0
      const currentCount = uniqueContent.length
      const hasMoreItems = currentCount < totalElements

      setPagination((prev) => ({
        ...prev,
        hasMore: hasMoreItems,
        page: 0,
        totalElements: totalElements,
      }))
    } catch (error) {
      console.error(`Failed to filter tournaments by sport ${sportType}:`, error)
      setEventsData((prev) => ({ ...prev, loading: false }))
      setPagination((prev) => ({ ...prev, loadingMore: false }))
    }
  }

  // Custom load function for desktop table that captures the data for mobile
  const handleLoadEvents = async (state, table, query) => {
    // For desktop table, just pass through
    const result = await loadEvents(state, table, query)

    // Don't update mobile data - mobile uses its own tournament endpoint

    return result
  }

  return (
    <Panel>
      <Drawer
        isActive={appConfig.drawerState !== ''}
        hideDrawer={() => appConfig.toggleDrawer('')}
      >
        {appConfig.drawerState !== '' && (
          <MakeInWinAdd
            match={{
              params: {
                eventId: appConfig.drawerState,
              },
            }}
            onClose={() => appConfig.toggleDrawer('')}
          />
        )}
      </Drawer>

      {/* Mobile/Tablet View */}
      <div css={styles.mobileView}>
        <MobileHeader
          title={title}
          selectedSportType={mobileSelectedSport}
          onSportTypeChange={handleMobileSportChange}
        />

        <div css={styles.contentContainer}>
          <ContentLoader loading={eventsData.loading}>
            <div css={styles.cardsContainer}>
              {eventsData.content.map((event, index) => (
                <MobileTournamentCard
                  key={event.id || `event-${index}`}
                  event={event}
                />
              ))}
              {eventsData.content.length === 0 && !eventsData.loading && (
                <div css={styles.emptyState}>
                  <Text size={16} css={styles.emptyText}>
                    No tournaments found
                  </Text>
                </div>
              )}
              {pagination.loadingMore && (
                <div css={styles.loadingMore}>
                  <ContentLoader loading={true}>
                    <div css={styles.loadingPlaceholder}>
                      <Text size={14} css={styles.loadingText}>
                        Loading more tournaments...
                      </Text>
                    </div>
                  </ContentLoader>
                </div>
              )}
            </div>
          </ContentLoader>
        </div>
      </div>

      {/* Desktop View */}
      <div css={styles.desktopView}>
        <div css={styles.desktopHeader}>
          <div css={styles.desktopHeaderTitle}>
            <Margin top={1} bottom={2}>
              <Text size={18} bold white>
                Upcoming events
              </Text>
            </Margin>
          </div>
          <div css={styles.desktopHeaderFilters}>
            <EventsFilters debounce={250} onSearch={onSearch} />
          </div>
        </div>

        <div css={styles.desktopTableSection}>
          <Table
            id="events-table"
            className="-highlight"
            columns={columns}
            loadData={handleLoadEvents}
          />
        </div>
      </div>
    </Panel>
  )
}

MobileLayoutComponent.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape()),
  loadEvents: PropTypes.func.isRequired,
  loadUpcomingTournaments: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  appConfig: PropTypes.shape(),
}

export default observer(MobileLayoutComponent)
